const HDR_QUERY = '(video-dynamic-range: high), (dynamic-range: high)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const QUERY_PARAMS = new URLSearchParams(window.location.search);
const FORCE_HDR_PREVIEW = QUERY_PARAMS.get('hdr') === '1';
const SHOW_SOURCE_DEBUG = QUERY_PARAMS.get('hdr-debug') === '1';
const HDR_FIRST_FRAME_TIMEOUT_MS = 8000;
const HERO_PLAYBACK_RATE = 0.85;
const firstFrameTimers = new WeakMap();

const candidates = [
  {
    key: 'hevc',
    datasetKey: 'zenwuHdrHevc',
    contentType: 'video/mp4; codecs="hvc1"',
    bitrate: 4_800_000,
  },
  {
    key: 'vp9',
    datasetKey: 'zenwuHdrVp9',
    contentType: 'video/webm; codecs="vp09.02.40.10.01.09.18.09.00"',
    bitrate: 6_100_000,
  },
];

async function canDecode(video, candidate, codecOnly = false) {
  if (!video.canPlayType(candidate.contentType)) return false;
  if (codecOnly) return true;

  if (!navigator.mediaCapabilities?.decodingInfo) return true;

  try {
    const result = await navigator.mediaCapabilities.decodingInfo({
      type: 'file',
      video: {
        contentType: candidate.contentType,
        width: 1920,
        height: 1080,
        bitrate: candidate.bitrate,
        framerate: 25,
      },
    });

    return result.supported && result.smooth;
  } catch {
    return true;
  }
}

async function chooseSource(video) {
  const hdrDisplay = window.matchMedia(HDR_QUERY).matches;
  const saveData = navigator.connection?.saveData === true;

  if ((hdrDisplay || FORCE_HDR_PREVIEW) && !saveData) {
    for (const candidate of candidates) {
      if (
        video.dataset[candidate.datasetKey] &&
        (await canDecode(video, candidate, FORCE_HDR_PREVIEW))
      ) {
        return {
          key: candidate.key,
          url: video.dataset[candidate.datasetKey],
        };
      }
    }
  }

  return { key: 'sdr', url: video.dataset.zenwuSdr };
}

async function chooseNextSource(video, failedKey) {
  const failedIndex = candidates.findIndex((candidate) => candidate.key === failedKey);

  for (const candidate of candidates.slice(failedIndex + 1)) {
    if (
      video.dataset[candidate.datasetKey] &&
      (await canDecode(video, candidate, FORCE_HDR_PREVIEW))
    ) {
      return {
        key: candidate.key,
        url: video.dataset[candidate.datasetKey],
      };
    }
  }

  return { key: 'sdr', url: video.dataset.zenwuSdr };
}

function showSourceDebug(video, key) {
  if (!SHOW_SOURCE_DEBUG) return;

  let label = video.parentElement.querySelector('[data-zenwu-hdr-debug]');
  if (!label) {
    label = document.createElement('output');
    label.dataset.zenwuHdrDebug = '';
    label.style.cssText = [
      'position:absolute',
      'top:16px',
      'left:16px',
      'z-index:20',
      'padding:8px 12px',
      'border-radius:4px',
      'background:rgba(32,34,25,.88)',
      'color:#F6EDDD',
      'font:600 13px/1.2 sans-serif',
      'letter-spacing:.02em',
      'pointer-events:none',
    ].join(';');
    video.parentElement.append(label);
  }

  label.textContent = key === 'sdr'
    ? 'SDR fallback: no compatible HDR path'
    : `HDR ${key.toUpperCase()} active${FORCE_HDR_PREVIEW ? ' (preview override)' : ''}`;
}

function markActiveSource(video, key) {
  const slide = video.closest('slideshow-slide');
  if (!slide) return;

  if (key === 'sdr') {
    slide.removeAttribute('data-zenwu-hdr-active');
  } else {
    slide.dataset.zenwuHdrActive = key;
  }
}

function nextLoadGeneration(video) {
  const generation = Number(video.dataset.zenwuLoadGeneration || 0) + 1;
  video.dataset.zenwuLoadGeneration = String(generation);
  return generation;
}

function isCurrentLoadGeneration(video, generation) {
  return Number(video.dataset.zenwuLoadGeneration) === generation;
}

async function loadSource(video, source) {
  if (!source.url) {
    video.dataset.zenwuSelected = 'sdr';
    markActiveSource(video, 'sdr');
    showSourceDebug(video, 'sdr');
    video.pause();
    video.removeAttribute('src');
    video.load();
    video.hidden = false;
    return;
  }
  if (video.dataset.zenwuSelected === source.key) return;

  window.clearTimeout(firstFrameTimers.get(video));
  firstFrameTimers.delete(video);

  const currentTime = Number.isFinite(video.currentTime) ? video.currentTime : 0;
  const isHdr = source.key !== 'sdr';
  video.hidden = true;
  video.dataset.zenwuSelected = source.key;
  markActiveSource(video, source.key);
  showSourceDebug(video, source.key);

  if (isHdr) {
    video.preload = 'auto';
    video.addEventListener(
      'loadeddata',
      () => {
        if (video.dataset.zenwuSelected !== source.key) return;
        window.clearTimeout(firstFrameTimers.get(video));
        firstFrameTimers.delete(video);
        video.hidden = false;
      },
      { once: true },
    );
    firstFrameTimers.set(
      video,
      window.setTimeout(
        () => loadNextSource(video, source.key),
        HDR_FIRST_FRAME_TIMEOUT_MS,
      ),
    );
  } else {
    video.preload = 'metadata';
  }

  video.src = source.url;
  video.load();
  if (!isHdr) video.hidden = false;

  video.addEventListener(
    'loadedmetadata',
    () => {
      if (
        video.dataset.zenwuSelected === source.key &&
        currentTime > 0 &&
        currentTime < video.duration
      ) {
        video.currentTime = currentTime;
      }
    },
    { once: true },
  );

  if (!window.matchMedia(REDUCED_MOTION_QUERY).matches) {
    try {
      await video.play();
    } catch {
      // Preload and the first-frame timeout still provide a visible fallback.
    }
  }
}

async function loadNextSource(video, failedKey) {
  if (!failedKey || failedKey === 'sdr') return;

  const generation = nextLoadGeneration(video);
  const nextSource = await chooseNextSource(video, failedKey);
  if (
    !isCurrentLoadGeneration(video, generation) ||
    video.dataset.zenwuSelected !== failedKey
  ) {
    return;
  }

  video.dataset.zenwuSelected = '';
  await loadSource(video, nextSource);
}

async function loadBestSource(video) {
  const generation = nextLoadGeneration(video);
  const source = await chooseSource(video);
  if (!isCurrentLoadGeneration(video, generation)) return;

  await loadSource(video, source);
}

for (const video of document.querySelectorAll('[data-zenwu-hdr-video]')) {
  video.defaultPlaybackRate = HERO_PLAYBACK_RATE;
  video.playbackRate = HERO_PLAYBACK_RATE;

  if (window.matchMedia(REDUCED_MOTION_QUERY).matches) {
    video.autoplay = false;
    video.removeAttribute('autoplay');
  }

  video.addEventListener('error', async () => {
    const failedKey = video.dataset.zenwuSelected;
    await loadNextSource(video, failedKey);
  });

  loadBestSource(video);
}

window.matchMedia(HDR_QUERY).addEventListener('change', () => {
  for (const video of document.querySelectorAll('[data-zenwu-hdr-video]')) {
    loadBestSource(video);
  }
});

window.matchMedia(REDUCED_MOTION_QUERY).addEventListener('change', (event) => {
  for (const video of document.querySelectorAll('[data-zenwu-hdr-video]')) {
    if (event.matches) {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.pause();
    } else {
      video.autoplay = true;
      video.setAttribute('autoplay', '');
      video.play().catch(() => {});
    }
  }
});
